import { useSearchParams } from 'react-router-dom';
import { useGetMeApi } from 'eds-common/api/v2';
import { type MetricV2Type } from '@data/hooks/api/use-metrics-v2-api/use-metrics-v2-api';
import { useMetricsDatasetV2Api } from '@data/hooks/api/use-metrics-dataset-v2-api/use-metrics-dataset-v2-api';
import { useReportProficiencyGraphProps } from '@data/hooks/use-report-proficiency-graph-props/use-report-proficiency-graph-props';
import { useReportOutletContext } from '@data/pages/ReportLayout/ReportLayout';
import { getEdApps } from '@data/utils/get-ed-apps';
import { ReportHighlights } from '@data/components/report/ReportHighlights/ReportHighlights';
import { ReportProficiencyGraph } from '@data/components/report/ReportProficiencyGraph/ReportProficiencyGraph';
import { Table } from '@data/components/basic/Table/Table';
import {
  useAssessmentReportTable,
  type UseAssessmentReportTableProps,
} from '@data/hooks/use-report-tables/use-assessment-report-table/use-assessment-report-table';
import { Popover } from '@scholastic/mockingjay';
import InfoIcon from '@data/assets/bold-circle-info.svg';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface AssessmentReportPageProps {
  primaryMetricTypes: MetricV2Type[];
  proficiencyMetricType: string;
  proficiencyGraphOptions?: { xFieldKey?: string };
  tableConfig: UseAssessmentReportTableProps;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function AssessmentReportPage({
  primaryMetricTypes,
  proficiencyMetricType,
  proficiencyGraphOptions,
  tableConfig,
}: AssessmentReportPageProps) {
  const { dateRange, productId: edApp } = useReportOutletContext();
  const { data: user } = useGetMeApi();
  const [searchParams] = useSearchParams();
  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');

  // ── Proficiency Graph ────────────────────────────────────────────────────

  const { data, isLoading, error } = useMetricsDatasetV2Api({
    orgId: user?.currentOrg?.orgId ?? '',
    metricType: proficiencyMetricType,
    teacherIds: [user?.userId ?? ''],
    startDate: dateRange.from,
    endDate: dateRange.to,
    edApps: getEdApps(edApp),
    classIds: classIds.length > 0 ? classIds : null,
    studentIds: studentIds.length > 0 ? studentIds : null,
    enabled: !!user?.userId && !!user?.currentOrg?.orgId && !!edApp,
  });

  const {
    rows,
    proficiencyOrder,
    presentAssessmentPeriods,
    assessmentPeriodMonths,
    axisLabels,
  } = useReportProficiencyGraphProps(data, proficiencyGraphOptions);

  // ── Table ──────────────────────────────────────────────────────────────────

  const {
    showTable,
    tableProps,
    tableData,
    tableMetricsLoading,
    flatColumns,
    columnGroups,
    totalCount,
    handleExportToCSV,
    handleDrilldown,
    tableMetricsError,
  } = useAssessmentReportTable(tableConfig);

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-6">
      <ReportHighlights primaryMetricTypes={primaryMetricTypes} />
      <ReportProficiencyGraph
        loading={isLoading}
        label={data?.label}
        description={data?.description}
        rows={rows}
        proficiencyOrder={proficiencyOrder}
        assessmentPeriodMonths={assessmentPeriodMonths}
        presentAssessmentPeriods={presentAssessmentPeriods}
        axisLabels={axisLabels}
        error={error}
      />
      {showTable && (
        <>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-neutral-1300 tablet:text-2xl">
              Details
            </span>
            {tableData?.toolTips && (
              <Popover
                trigger={
                  <button
                    type="button"
                    aria-label="Table information"
                    className="flex items-center"
                  >
                    <InfoIcon aria-hidden="true" className="h-6 w-6" />
                  </button>
                }
              >
                {tableData.toolTips}
              </Popover>
            )}
          </div>
          <Table
            {...tableProps}
            loading={tableMetricsLoading}
            columns={flatColumns}
            rows={tableData?.rows ?? []}
            totalCount={totalCount}
            columnGroups={columnGroups}
            borderedCells
            onExportToCSV={handleExportToCSV}
            onDrilldown={handleDrilldown}
            error={tableMetricsError}
          />
        </>
      )}
    </div>
  );
}
